import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Phone, ExternalLink, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloatingButton, { WHATSAPP_NUMBER } from "@/components/WhatsAppFloatingButton";
import ListingsGrid from "@/components/ListingsGrid";
import ScheduleVisitDialog from "@/components/ScheduleVisitDialog";
import LeadQualificationSection from "@/components/LeadQualificationSection";
import { THANK_YOU } from "@/lib/thankYou";
import FaqSection, { type FaqItem } from "@/components/FaqSection";
import { Button } from "@/components/ui/button";
import { fetchListings, type ListingFilters } from "@/lib/listings";
import { supabase } from "@/integrations/supabase/client";
import { usePageMeta } from "@/hooks/usePageMeta";

export interface SemanticLandingProps {
  h1: string;
  slug: string; // e.g. "studios-airbnb-copacabana"
  metaDescription: string;
  keywords: string;
  intro: string; // paragraph shown under H1
  sections: { title: string; body: string }[];
  filters: ListingFilters;
  whatsappMessage: string;
  googleFormUrl?: string; // if empty, hides form CTA
  faq: FaqItem[];
  heroImage?: string;
}

const SemanticLanding = ({
  h1,
  slug,
  metaDescription,
  keywords,
  intro,
  sections,
  filters,
  whatsappMessage,
  googleFormUrl,
  faq,
  heroImage,
}: SemanticLandingProps) => {
  usePageMeta({ description: metaDescription, keywords });

  // Conteúdo editável no admin (aba "Landing Geral"): sobrescreve os textos padrão.
  const { data: cms } = useQuery({
    queryKey: ["landing-page-content", slug],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("landing_pages").select("content").eq("slug", slug).maybeSingle();
      return (data?.content || {}) as {
        hero_title?: string;
        hero_subtitle?: string;
        sections?: { title: string; body: string }[];
        faq?: FaqItem[];
      };
    },
  });

  const heading = cms?.hero_title || h1;
  const heroText = cms?.hero_subtitle || intro;
  const bodySections = cms?.sections?.length ? cms.sections : sections;
  const faqItems = cms?.faq?.length ? cms.faq : faq;

  const { data: listings = [], isLoading } = useQuery({
    queryKey: ["semantic-landing", slug, filters],
    queryFn: () => fetchListings(filters),
  });

  useEffect(() => {
    const url = `https://topclassimoveis.com/${slug}`;
    const collection = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: h1,
      description: metaDescription,
      url,
    };
    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://topclassimoveis.com/" },
        { "@type": "ListItem", position: 2, name: h1, item: url },
      ],
    };
    const s1 = document.createElement("script");
    s1.type = "application/ld+json";
    s1.text = JSON.stringify(collection);
    const s2 = document.createElement("script");
    s2.type = "application/ld+json";
    s2.text = JSON.stringify(breadcrumbs);
    document.head.appendChild(s1);
    document.head.appendChild(s2);
    return () => {
      document.head.removeChild(s1);
      document.head.removeChild(s2);
    };
  }, [slug, h1, metaDescription]);

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="relative bg-navy py-20 md:py-32 overflow-hidden">
          {heroImage && (
            <img
              src={heroImage}
              alt={heading}
              className="absolute inset-0 w-full h-full object-cover opacity-40"
              loading="eager"
              fetchPriority="high"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-navy/90" />
          <div className="relative container mx-auto px-4 text-center">
            <h1 className="font-display text-4xl md:text-6xl font-bold text-white mb-4 max-w-4xl mx-auto">
              {heading}
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-body max-w-2xl mx-auto mb-8">
              {heroText}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button variant="whatsapp" size="lg" asChild>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <Phone className="h-5 w-5" /> Falar no WhatsApp
                </a>
              </Button>
              {googleFormUrl && (
                <Button variant="gold" size="lg" asChild>
                  <a href={googleFormUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-5 w-5" /> Quero receber ofertas
                  </a>
                </Button>
              )}
              <Link to="/">
                <Button variant="outline" size="lg" className="bg-white/10 text-white border-white/40 hover:bg-white/20">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Home
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Content sections */}
        <section className="container mx-auto px-4 py-14 max-w-4xl">
          {bodySections.map((s, i) => (
            <div key={i} className="mb-10">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">{s.title}</h2>
              <p className="font-body text-foreground/85 leading-relaxed whitespace-pre-line">{s.body}</p>
            </div>
          ))}
        </section>

        {/* Listings with pre-applied filters */}
        <ListingsGrid listings={listings} isLoading={isLoading} />

        <LeadQualificationSection
          defaultNeighborhood={filters.neighborhood || undefined}
          thankYouPath={THANK_YOU.landing}
        />

        {/* CTA de agendamento */}
        <section className="py-12 bg-background border-y border-border">
          <div className="container mx-auto px-4 text-center">
            <ScheduleVisitDialog
              propertyName={heading}
              neighborhood={filters.neighborhood || null}
              label="Quero conhecer este imóvel"
              className="text-lg px-10 py-6"
            />
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-navy py-14">
          <div className="container mx-auto px-4 text-center">
            <h2 className="font-display text-3xl font-bold text-white mb-3">
              Encontrou algo que te interessa?
            </h2>
            <p className="text-white/80 font-body mb-6 max-w-xl mx-auto">
              Fale com nossos corretores especializados. Atendimento imediato via WhatsApp.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button variant="whatsapp" size="lg" asChild>
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                  <Phone className="h-5 w-5" /> Falar no WhatsApp
                </a>
              </Button>
              {googleFormUrl && (
                <Button variant="gold" size="lg" asChild>
                  <a href={googleFormUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-5 w-5" /> Preencher formulário
                  </a>
                </Button>
              )}
            </div>
          </div>
        </section>

        <FaqSection scriptId={`landing-${slug}`} title="Perguntas Frequentes" items={faqItems} />
      </main>
      <Footer />
      <WhatsAppFloatingButton />
    </div>
  );
};

export default SemanticLanding;
